import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { uploadImage, resolveImg } from "@/lib/apiData";

const emptyForm = (fields) => Object.fromEntries(fields.map((f) => [f.name, f.type === "checkbox" ? false : ""]));

export const CrudManager = ({ title, queryKey, fetchFn, api, columns, fields, idKey = "id", extraInvalidateKeys = [] }) => {
  const qc = useQueryClient();
  const { data: rows = [] } = useQuery({ queryKey: [queryKey], queryFn: fetchFn });
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm(fields));
  const [uploading, setUploading] = useState(null);

  const handleUpload = async (name, file) => {
    if (!file) return;
    setUploading(name);
    try {
      const res = await uploadImage(file);
      setForm((prev) => ({ ...prev, [name]: res.url }));
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(null);
    }
  };

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: [queryKey] });
    extraInvalidateKeys.forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  };

  const createMut = useMutation({ mutationFn: api.create, onSuccess: () => { invalidate(); toast.success(`${title} created`); setOpenForm(false); }, onError: (e) => toast.error(e.response?.data?.detail || "Error") });
  const updateMut = useMutation({ mutationFn: ({ id, payload }) => api.update(id, payload), onSuccess: () => { invalidate(); toast.success(`${title} updated`); setOpenForm(false); }, onError: (e) => toast.error(e.response?.data?.detail || "Error") });
  const deleteMut = useMutation({ mutationFn: api.remove, onSuccess: () => { invalidate(); toast.success(`${title} deleted`); }, onError: (e) => toast.error(e.response?.data?.detail || "Error") });

  const openCreate = () => { setEditing(null); setForm(emptyForm(fields)); setOpenForm(true); };
  const openEdit = (row) => {
    setEditing(row);
    const f = {};
    fields.forEach((fl) => {
      const v = row[fl.name];
      f[fl.name] = fl.type === "tags" ? (Array.isArray(v) ? v.join(", ") : v || "") : v ?? (fl.type === "checkbox" ? false : "");
    });
    setForm(f);
    setOpenForm(true);
  };

  const submit = (e) => {
    e.preventDefault();
    const payload = {};
    fields.forEach((fl) => {
      let v = form[fl.name];
      if (fl.type === "number") v = v === "" ? 0 : Number(v);
      else if (fl.type === "tags") v = String(v || "").split(",").map((s) => s.trim()).filter(Boolean);
      else if (fl.type === "checkbox") v = Boolean(v);
      payload[fl.name] = v;
    });
    if (editing) updateMut.mutate({ id: editing[idKey], payload });
    else createMut.mutate(payload);
  };

  return (
    <div data-testid={`admin-${queryKey}-panel`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-3xl font-black text-neutral-900">{title}</h2>
        <Button onClick={openCreate} data-testid={`add-${queryKey}-btn`} className="bg-[#d00000] hover:bg-[#a30000] text-white rounded-none gap-2"><Plus className="w-4 h-4" /> Add</Button>
      </div>

      <div className="border border-neutral-200 bg-white overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((c) => <TableHead key={c.key} className="text-neutral-500 uppercase text-xs tracking-wider">{c.label}</TableHead>)}
              <TableHead className="text-right text-neutral-500 uppercase text-xs tracking-wider">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row[idKey]} data-testid={`${queryKey}-row-${row[idKey]}`}>
                {columns.map((c) => <TableCell key={c.key} className="text-neutral-800 align-middle">{c.render ? c.render(row) : String(row[c.key] ?? "")}</TableCell>)}
                <TableCell className="text-right whitespace-nowrap">
                  <button onClick={() => openEdit(row)} data-testid={`edit-${queryKey}-${row[idKey]}`} className="inline-flex w-8 h-8 items-center justify-center text-neutral-500 hover:text-[#d00000]"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => { if (window.confirm("Delete this item?")) deleteMut.mutate(row[idKey]); }} data-testid={`delete-${queryKey}-${row[idKey]}`} className="inline-flex w-8 h-8 items-center justify-center text-neutral-500 hover:text-[#d00000]"><Trash2 className="w-4 h-4" /></button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {rows.length === 0 && <p className="p-8 text-center text-neutral-400">No items yet.</p>}
      </div>

      <Dialog open={openForm} onOpenChange={setOpenForm}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid={`${queryKey}-form-dialog`}>
          <DialogHeader><DialogTitle className="font-display text-2xl text-neutral-900">{editing ? "Edit" : "New"} {title}</DialogTitle>
            <DialogDescription className="sr-only">Fill in the fields and save.</DialogDescription></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            {fields.map((fl) => (
              <div key={fl.name}>
                <Label className="text-neutral-500 text-xs uppercase tracking-widest">{fl.label}</Label>
                {fl.type === "textarea" ? (
                  <Textarea value={form[fl.name]} onChange={(e) => setForm({ ...form, [fl.name]: e.target.value })} rows={4} data-testid={`field-${fl.name}`} className="mt-1 bg-[#fbf9f7] border-neutral-300 rounded-none focus-visible:ring-[#d00000]" />
                ) : fl.type === "image" ? (
                  <div className="mt-1 flex items-center gap-4">
                    {form[fl.name] ? (
                      <img src={resolveImg(form[fl.name])} alt="preview" className="w-20 h-20 object-cover border border-neutral-200" data-testid={`preview-${fl.name}`} />
                    ) : (
                      <div className="w-20 h-20 bg-neutral-100 border border-dashed border-neutral-300 flex items-center justify-center text-neutral-400 text-xs">No image</div>
                    )}
                    <label className="inline-flex items-center gap-2 h-11 px-4 border border-neutral-300 cursor-pointer hover:border-[#d00000] text-sm text-neutral-700">
                      {uploading === fl.name ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {form[fl.name] ? "Change image" : "Upload image"}
                      <input type="file" accept="image/*" className="hidden" data-testid={`field-${fl.name}`} onChange={(e) => handleUpload(fl.name, e.target.files[0])} />
                    </label>
                  </div>
                ) : fl.type === "select" ? (
                  <select value={form[fl.name]} onChange={(e) => setForm({ ...form, [fl.name]: e.target.value })} data-testid={`field-${fl.name}`} className="mt-1 w-full h-11 px-3 bg-[#fbf9f7] border border-neutral-300 rounded-none focus:outline-none focus:ring-2 focus:ring-[#d00000]">
                    <option value="">Select…</option>
                    {(fl.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ) : fl.type === "checkbox" ? (
                  <div className="mt-2"><input type="checkbox" checked={!!form[fl.name]} onChange={(e) => setForm({ ...form, [fl.name]: e.target.checked })} data-testid={`field-${fl.name}`} className="w-5 h-5 accent-[#d00000]" /></div>
                ) : (
                  <Input type={fl.type === "number" ? "number" : "text"} step="any" value={form[fl.name]} onChange={(e) => setForm({ ...form, [fl.name]: e.target.value })} placeholder={fl.placeholder} data-testid={`field-${fl.name}`} className="mt-1 h-11 bg-[#fbf9f7] border-neutral-300 rounded-none focus-visible:ring-[#d00000]" />
                )}
              </div>
            ))}
            <DialogFooter>
              <Button type="submit" data-testid={`${queryKey}-save-btn`} className="bg-[#d00000] hover:bg-[#a30000] text-white rounded-none">{editing ? "Save changes" : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
